import {
  TableContainer,
  Table,
  Thead,
  Tr,
  Th,
  Td,
  Tbody,
  HStack,
  Icon
} from '@chakra-ui/react';
import React, { useEffect, useState } from 'react';
import {
  chooseDataStateType,
  MaintenanceModalDispLabels
} from '../../../molecules/MaintenanceModalDispLabels';
import { MaintenanceFooter } from '../MaintenanceFooter';
import {
  ArrowUpDownIcon,
  TriangleDownIcon,
  TriangleUpIcon
} from '@chakra-ui/icons';
import { QueryMailingListApi } from '../../../../apis/QueryMaintenance.api';
import { MailingListSearchCondition } from '../../../../pages/MailingListMaintenance';

const TableColumnEnum = {
  id: 'id',
  email: 'email',
  enable: 'enable',
  created_at: 'created_at',
  updated_at: 'updated_at'
};

export interface MailingListType {
  id: number;
  email: string;
  enable: boolean;
  created_at: string;
  updated_at: string;
}

interface props {
  MailingListDatas: MailingListType[] | undefined;
  setMaintenanceDatas: React.Dispatch<
    React.SetStateAction<MailingListType[] | undefined>
  >;
  setChooseData: (data: chooseDataStateType) => void;
  onOpenBrowse: () => void;
  onOpenEdit: () => void;
  onToggle: () => void;
  Query: MailingListSearchCondition;
  setQuery: React.Dispatch<React.SetStateAction<MailingListSearchCondition>>;
  createOrUpdate: boolean;
  searchSwitch: boolean;
}

const headers = [
  { label: 'ID', key: 'id' },
  { label: 'メールアドレス', key: 'email' },
  { label: '有効', key: 'enable' },
  { label: '登録日時', key: 'created_at' },
  { label: '更新日時', key: 'updated_at' }
];

const GetCsvFileName = () => {
  const date = new Date();
  const hour =
    date.getMonth().toString().length === 1
      ? '0' + (date.getMonth() + 1).toString()
      : date.getMonth().toString();
  const day =
    date.getDate().toString().length === 1
      ? '0' + date.getDate().toString()
      : date.getDate().toString();
  return 'maillinglists-' + date.getFullYear() + '-' + hour + '-' + day;
};

export const MaintenanceMailingListTable: React.FC<props> = (props) => {
  const {
    MailingListDatas,
    setMaintenanceDatas,
    setChooseData,
    onOpenBrowse,
    onOpenEdit,
    onToggle,
    Query,
    setQuery,
    createOrUpdate,
    searchSwitch
  } = props;

  const [pageNow, setPageNow] = useState<number>(1);
  const [columnSortType, setColumnSortType] = useState<{
    key: string;
    sort: boolean;
  }>({ key: '', sort: false });
  const pageDispMax = 30;
  const pageMax = 6;
  const csvFileName = GetCsvFileName();
  const GetPageCount = (): number => {
    if (MailingListDatas !== undefined) {
      const divisionCount = Math.floor(MailingListDatas.length / pageDispMax);
      const remCount = MailingListDatas.length % pageDispMax === 0 ? 0 : 1;
      return divisionCount + remCount;
    }
    return 1;
  };

  const ColumnNameTriangleType = (column: string) => {
    if (columnSortType.key === column) {
      if (columnSortType.sort) {
        return TriangleUpIcon;
      } else {
        return TriangleDownIcon;
      }
    }
    return ArrowUpDownIcon;
  };

  const ColumnSortApi = async (column: string) => {
    const params = new URLSearchParams();
    const sortType = !columnSortType.sort;
    params.append('order', column + (sortType ? '_asc' : '_desc'));
    Query.email !== '' && params.append('email_contains', Query.email);
    Query.enable && params.append('enable', 'true');
    Query.dateCreatedFrom !== '' &&
      params.append('created_at_gte', Query.dateCreatedFrom);
    Query.dateCreatedTo !== '' &&
      params.append('created_at_lt', Query.dateCreatedTo);
    Query.dateUpdatedFrom !== '' &&
      params.append('updated_at_gte', Query.dateUpdatedFrom);
    Query.dateUpdatedTo !== '' &&
      params.append('updated_at_lt', Query.dateUpdatedTo);
    const query = '/?' + params.toString();

    setQuery((pre) => ({ ...pre, sort: { key: column, sortType: sortType } }));
    const response = await QueryMailingListApi(query);
    if (response.success && response.data !== null) {
      setMaintenanceDatas(response.data);
      setColumnSortType({ key: column, sort: sortType });
    } else {
      alert(column + 'のソートに失敗しました。');
    }
  };

  useEffect(() => {
    setPageNow(1);
  }, [searchSwitch]);

  useEffect(() => {
    setPageNow(1);
    setColumnSortType({ key: '', sort: false });
  }, [createOrUpdate]);

  return (
    <>
      <TableContainer
        position='absolute'
        h='100%'
        w='100%'
        ml={5}
        overflowY='auto'
        gridRow='2/3'
        gridColumn='1/2'
        overflowX='unset'
      >
        <Table variant='simple'>
          <Thead borderWidth='1px' position='sticky' top={0} zIndex='docked'>
            <Tr bg='gray.800'>
              <Th
                cursor='pointer'
                textAlign='center'
                borderWidth='1px'
                color='white'
                onClick={() => ColumnSortApi(TableColumnEnum.id)}
              >
                <HStack>
                  <label style={{ cursor: 'pointer' }}>ID</label>
                  <Icon
                    ml={1}
                    as={ColumnNameTriangleType(TableColumnEnum.id)}
                  />
                </HStack>
              </Th>
              <Th
                cursor='pointer'
                textAlign='center'
                borderWidth='1px'
                color='white'
                onClick={() => ColumnSortApi(TableColumnEnum.email)}
              >
                <HStack>
                  <label style={{ cursor: 'pointer' }}>メールアドレス</label>
                  <Icon
                    ml={1}
                    as={ColumnNameTriangleType(TableColumnEnum.email)}
                  />
                </HStack>
              </Th>
              <Th textAlign='center' borderWidth='1px' color='white'>
                <HStack>
                  <label>有効</label>
                </HStack>
              </Th>
              <Th textAlign='center' borderWidth='1px' color='white'>
                <HStack>
                  <label>登録日時</label>
                </HStack>
              </Th>
              <Th textAlign='center' borderWidth='1px' color='white'>
                <HStack>
                  <label>更新日時</label>
                </HStack>
              </Th>
              <Th textAlign='center' borderWidth='1px' color='white'></Th>
            </Tr>
          </Thead>
          <Tbody>
            {MailingListDatas !== undefined &&
              MailingListDatas.map((data, i) => {
                if (
                  pageDispMax * pageNow - pageDispMax <= i &&
                  i < pageDispMax * pageNow
                ) {
                  return (
                    <Tr key={`${'mailingList_' + i}`}>
                      <Td>{data.id}</Td>
                      <Td>{data.email}</Td>
                      <Td>{data.enable === true ? 'Yes' : 'No'}</Td>
                      <Td>{data.created_at}</Td>
                      <Td>{data.updated_at}</Td>
                      <Td>
                        <MaintenanceModalDispLabels
                          data={{ type: 'mailingList', mailingList: data }}
                          setChooseData={setChooseData}
                          onOpenBrowse={onOpenBrowse}
                          onOpenEdit={onOpenEdit}
                          onToggle={onToggle}
                        />
                      </Td>
                    </Tr>
                  );
                }
              })}
          </Tbody>
        </Table>
      </TableContainer>
      <MaintenanceFooter
        CsvData={MailingListDatas !== undefined ? MailingListDatas : []}
        DataLength={MailingListDatas?.length}
        Headers={headers}
        CsvFileName={csvFileName}
        GetPageCount={GetPageCount}
        pageMax={pageMax}
        pageNow={pageNow}
        setPageNow={setPageNow}
      />
    </>
  );
};
